/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/** `/settings/email-log`: e-mails the platform sent or tried to send, with what was sent and "send again". */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import type { NotificationRow, NotificationStatus } from '../../api/areas/notify';
import { Badge, type Tone } from '../../components/Badge';
import { Callout, DataTable, Field, FilterBar, Section, type Column } from '../../components/blocks';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';

type Lookup = (key: string, options?: Record<string, unknown>) => string;

const STATUS_TONE: Record<NotificationStatus, Tone> = { PENDING: 'pending', SENDING: 'pending', SENT: 'ok', FAILED: 'crit', SUPPRESSED: 'neutral' };
const STATUSES: NotificationStatus[] = ['PENDING', 'SENDING', 'SENT', 'FAILED', 'SUPPRESSED'];

const selectClass =
  'h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30';

function Detail({ id, onChanged }: { id: string; onChanged: () => void }) {
  const { t } = useTranslation('integrations');
  const tr = t as unknown as Lookup;
  const r = useApi((signal) => api.getNotification(id, { signal }), [id]);
  const resend = useWrite((_: null, opts) => api.resendNotification(id, opts));
  if (r.status === 'loading') return <Loading rows={3} />;
  if (r.status === 'error') return <WriteError error={r.error} />;
  const n = r.data;
  return (
    <div className="grid gap-3 text-sm">
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
        <dt className="text-muted-foreground">{t('email.log.status')}</dt>
        <dd>
          <Badge tone={STATUS_TONE[n.status]}>{t(`email.log.statuses.${n.status}`)}</Badge>
        </dd>
        <dt className="text-muted-foreground">{t('email.log.notice')}</dt>
        <dd>{tr(`email.keys.${n.template_key}`, { defaultValue: n.template_key })}</dd>
        <dt className="text-muted-foreground">{t('email.log.to')}</dt>
        <dd>{n.recipient_name ? `${n.recipient_name} <${n.recipient_email}>` : n.recipient_email}</dd>
        <dt className="text-muted-foreground">{t('email.log.from')}</dt>
        <dd>{n.sent_from ?? '—'}</dd>
        <dt className="text-muted-foreground">{t('email.log.attempts')}</dt>
        <dd>
          {n.attempts} / {n.max_attempts}
        </dd>
        <dt className="text-muted-foreground">{t('email.log.created')}</dt>
        <dd>{formatDateTime(n.created_at)}</dd>
        <dt className="text-muted-foreground">{t('email.log.sent')}</dt>
        <dd>{n.sent_at ? formatDateTime(n.sent_at) : '—'}</dd>
        {n.template_source ? (
          <>
            <dt className="text-muted-foreground">{t('email.log.template')}</dt>
            <dd>{t(`email.tpl.sources.${n.template_source}`)}</dd>
          </>
        ) : null}
      </dl>
      {n.last_error ? <Callout tone="warn">{n.last_error}</Callout> : null}
      {n.has_link ? <p className="text-xs text-muted-foreground">{t('email.log.linkNote')}</p> : null}
      {n.sent_subject ? (
        <Field label={t('email.log.whatWasSent')}>
          <div className="rounded border bg-muted/40 p-3">
            <p className="font-medium">{n.sent_subject}</p>
            <pre className="mt-2 whitespace-pre-wrap font-sans text-sm">{n.sent_text}</pre>
          </div>
        </Field>
      ) : null}
      {resend.state.phase === 'error' ? <WriteError error={resend.state.error} /> : null}
      {resend.state.phase === 'done' ? <Callout tone="ok">{t('email.log.requeued')}</Callout> : null}
      {n.status !== 'PENDING' && n.status !== 'SENDING' ? (
        <div>
          <Button
            type="button"
            variant="outline"
            disabled={resend.busy || resend.state.phase === 'done'}
            onClick={() =>
              void resend.run(null).then((x) => {
                if (x) {
                  r.reload();
                  onChanged();
                }
              })
            }
          >
            {t('email.log.resend')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function NotificationLogPage() {
  const { t } = useTranslation('integrations');
  const tr = t as unknown as Lookup;
  useDocumentTitle(t('email.log.title'));
  const [status, setStatus] = useState<NotificationStatus | ''>('');
  const [recipient, setRecipient] = useState('');
  const [applied, setApplied] = useState('');
  const [before, setBefore] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState<string | null>(null);
  const r = useApi(
    (signal) => api.listNotifications({ status: status || undefined, recipient: applied || undefined, before, limit: 50 }, { signal }),
    [status, applied, before],
  );
  const columns: Column<NotificationRow>[] = [
    { key: 'created', header: t('email.log.created'), className: 'whitespace-nowrap', cell: (n) => formatDateTime(n.created_at) },
    {
      key: 'notice',
      header: t('email.log.notice'),
      className: 'whitespace-normal',
      cell: (n) => (
        <button type="button" className="text-left text-primary hover:underline" onClick={() => setOpen(n.id)}>
          {tr(`email.keys.${n.template_key}`, { defaultValue: n.template_key })}
          {n.domain ? <span className="block text-xs text-muted-foreground">{n.domain}</span> : null}
        </button>
      ),
    },
    { key: 'to', header: t('email.log.to'), className: 'whitespace-normal', cell: (n) => <span className="break-all">{n.recipient_email}</span> },
    {
      key: 'status',
      header: t('email.log.status'),
      cell: (n) => (
        <div className="flex flex-col items-start gap-1">
          <Badge tone={STATUS_TONE[n.status]}>{t(`email.log.statuses.${n.status}`)}</Badge>
          {n.last_error && n.status !== 'SENT' ? <span className="max-w-64 truncate text-xs text-muted-foreground" title={n.last_error}>{n.last_error}</span> : null}
        </div>
      ),
    },
  ];
  return (
    <>
      <PageHeader title={t('email.log.title')} subtitle={t('email.log.subtitle')} crumbs={[{ label: t('email.crumb') }]} />
      <FilterBar>
        <Field label={t('email.log.status')}>
          <select
            className={selectClass}
            value={status}
            onChange={(e) => {
              setBefore(undefined);
              setStatus(e.target.value as NotificationStatus | '');
            }}
          >
            <option value="">{t('email.log.any')}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`email.log.statuses.${s}`)}
              </option>
            ))}
          </select>
        </Field>
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setBefore(undefined);
            setApplied(recipient.trim());
          }}
        >
          <Field label={t('email.log.recipient')}>
            <Input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="an@example.vn" />
          </Field>
          <Button type="submit" variant="outline" size="sm">
            {t('email.log.search')}
          </Button>
        </form>
      </FilterBar>
      <Section flush>
        {r.status === 'loading' ? (
          <div className="px-5">
            <Loading rows={5} />
          </div>
        ) : null}
        {r.status === 'error' ? (
          <div className="px-5 pb-5">
            <WriteError error={r.error} />
          </div>
        ) : null}
        {r.status === 'success' ? (
          <>
            <DataTable columns={columns} rows={r.data.items} rowKey={(n) => n.id} empty={<EmptyState title={t('email.log.empty')} />} />
            <div className="flex justify-end gap-2 px-5 py-3">
              {before ? (
                <Button type="button" variant="outline" size="sm" onClick={() => setBefore(undefined)}>
                  {t('email.log.first')}
                </Button>
              ) : null}
              {r.data.next_before ? (
                <Button type="button" variant="outline" size="sm" onClick={() => setBefore(r.data.next_before ?? undefined)}>
                  {t('email.log.older')}
                </Button>
              ) : null}
            </div>
          </>
        ) : null}
      </Section>
      <Dialog open={open !== null} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl [&>*]:min-w-0">
          <DialogHeader>
            <DialogTitle>{t('email.log.detailTitle')}</DialogTitle>
            <DialogDescription>{t('email.log.detailDescription')}</DialogDescription>
          </DialogHeader>
          {open ? <Detail id={open} onChanged={r.reload} /> : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
